import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const AVAILABLE_PRIVILEGES = [
  { id: 'extend_trials', label: 'Extend Free Trials' },
  { id: 'manage_limits', label: 'Manage AI & Risk Limits' },
  { id: 'view_audit_logs', label: 'View System Audit Logs' },
  { id: 'handle_reviews', label: 'Handle Expert Reviews' }
];

const COUNTRIES = [
  'India', 'United States', 'United Kingdom', 'Canada', 'Australia', 
  'Singapore', 'New Zealand', 'Japan', 'South Korea', 'Hong Kong', 
  'Germany', 'France', 'Netherlands', 'Ireland', 'Sweden', 
  'Switzerland', 'United Arab Emirates', 'Saudi Arabia', 'South Africa'
];

const convertToCSV = (arr) => {
  if (!arr || !arr.length) return '';
  const headers = Object.keys(arr[0]);
  const csvRows = [];
  
  csvRows.push(headers.map(h => `"${String(h).replace(/"/g, '""')}"`).join(','));
  
  for (const row of arr) {
    csvRows.push(headers.map(h => {
      const val = row[h] === null || row[h] === undefined ? '' : String(row[h]);
      return `"${val.replace(/"/g, '""')}"`;
    }).join(','));
  }
  return csvRows.join('\n');
};

export function FrameworkUpload() {
  const { token } = useAuth();
  const [selectedCountry, setSelectedCountry] = useState('');
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState('');

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedCountry || !file) return setStatus('Please select a country and a file.');

    const formData = new FormData();
    formData.append('country', selectedCountry);
    formData.append('file', file);

    try {
      setStatus('Uploading...');
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const res = await axios.post(`${baseURL}/api/compliance/upload`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      });
      setStatus(res.data.message);
      setFile(null);
    } catch (err) {
      setStatus('Upload failed: ' + (err.response?.data?.error || err.message));
    }
  };

  return (
    <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm max-w-xl mt-8">
      <h2 className="text-lg font-bold text-slate-800 mb-2">⚖️ Upload Compliance Frameworks</h2>
      <p className="text-sm text-slate-500 mb-4">Upload Markdown (.md) or Text (.txt) files containing the legal frameworks.</p>
      
      <form onSubmit={handleUpload} className="space-y-4">
        <select 
          className="w-full border border-slate-300 rounded-lg px-4 py-2 text-sm bg-slate-50 outline-none focus:border-blue-500"
          value={selectedCountry}
          onChange={(e) => setSelectedCountry(e.target.value)}
        >
          <option value="">-- Select Jurisdiction --</option>
          {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>

        <input 
          type="file" 
          accept=".md,.txt"
          onChange={(e) => setFile(e.target.files[0])}
          className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
        />

        <button type="submit" className="w-full bg-slate-900 text-white font-bold py-2.5 rounded-lg hover:bg-slate-800 transition-colors">
          Upload Framework
        </button>
        
        {status && <p className="text-sm font-medium text-blue-600 mt-2">{status}</p>}
      </form>
    </div>
  );
}

export default function AdminDashboard() {

  const { user, token, startImpersonation } = useAuth();
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [alertModal, setAlertModal] = useState({ show: false, title: '', message: '', type: 'success' });
  const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', onConfirm: null });
  const [previewModal, setPreviewModal] = useState({ show: false, data: [], file: null, uploadType: '' }); 
  const [responsesModal, setResponsesModal] = useState({ show: false, orgName: '', data: [] });

  const [selectedOrg, setSelectedOrg] = useState(null);
  const [extendForm, setExtendForm] = useState({ addDays: 14, newAiLimit: 10, newRiskLimit: 10 });
  const [promoteOrg, setPromoteOrg] = useState(null);
  const [selectedPrivileges, setSelectedPrivileges] = useState([]);

  const [activeTab, setActiveTab] = useState('orgs'); 
  const [leads, setLeads] = useState([]);
  const [graderLeads, setGraderLeads] = useState([]); 

  const [targetUploadOrg, setTargetUploadOrg] = useState('');
  const [uploadingCsv, setUploadingCsv] = useState(false);

  const fetchLeads = async () => {
    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const [leadsRes, graderRes] = await Promise.all([
        axios.get(`${baseURL}/api/leads`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${baseURL}/api/leads/grader-reports`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setLeads(leadsRes.data);
      setGraderLeads(graderRes.data);
    } catch (err) {
      console.error("Failed to fetch leads:", err.response ? err.response.data : err.message);
    }
  };

  useEffect(() => {
    if (!user || !user.is_system_admin) {
      navigate('/dashboard');
      return;
    }
    fetchOrgs();
    if (user.admin_role === 'SUPER_ADMIN') {
      fetchLeads();
    }
  }, [user, navigate]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const updateGraderStatus = async (id, newStatus) => {
    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      await axios.patch(`${baseURL}/api/leads/grader-reports/${id}/status`, 
        { status: newStatus }, 
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setGraderLeads(graderLeads.map(lead => 
        lead.id === id ? { ...lead, status: newStatus } : lead
      ));
    } catch (err) {
      setAlertModal({ show: true, title: 'Error', message: 'Failed to save status to database.', type: 'error' });
    }
  };

  const handleImpersonate = async (targetOrgId) => {
      try {
        const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const res = await axios.post(`${baseURL}/api/admin/impersonate`, { targetOrgId }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        startImpersonation(
            res.data.token, 
            targetOrgId, 
            res.data.orgName, 
            res.data.targetUserId,
            res.data.targetUserName
        );
        
        window.location.href = '/dashboard'; 
      } catch (err) {
        setAlertModal({ show: true, title: 'Error', message: 'Failed to initiate impersonation.', type: 'error' });
      }
  };

  const handleViewResponses = async (orgId, orgName) => {
    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const res = await axios.get(`${baseURL}/api/admin/sprint-answers/${orgId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResponsesModal({ show: true, orgName: orgName, data: res.data });
    } catch (err) {
      setAlertModal({ show: true, title: 'Error', message: 'Failed to fetch client responses.', type: 'error' });
    }
  };

  const parseCSVLocal = (str) => {
    const arr = [];
    let quote = false;
    let row = 0, col = 0;
    for (let c = 0; c < str.length; c++) {
        let cc = str[c], nc = str[c+1];
        arr[row] = arr[row] || [];
        arr[row][col] = arr[row][col] || '';
        if (cc === '"' && quote && nc === '"') { arr[row][col] += cc; ++c; continue; }
        if (cc === '"') { quote = !quote; continue; }
        if (cc === ',' && !quote) { ++col; continue; }
        if (cc === '\r' && nc === '\n' && !quote) { ++row; col = 0; ++c; continue; }
        if (cc === '\n' && !quote) { ++row; col = 0; continue; }
        if (cc === '\r' && !quote) { ++row; col = 0; continue; }
        arr[row][col] += cc;
    }
    return arr;
  };

  const handleFileSelectionForPreview = (event, uploadType) => {
    if ((uploadType === 'SPRINT' || uploadType === 'MATRIX') && !targetUploadOrg) {
        setAlertModal({ show: true, title: 'Missing Client', message: 'Please select a client organization first.', type: 'error' });
        event.target.value = '';
        return;
    }

    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const rows = parseCSVLocal(text);
      if(rows.length < 2) {
        setAlertModal({ show: true, title: 'Invalid CSV', message: 'The CSV file appears to be empty or invalid.', type: 'error' });
        return;
      }
      
      const headers = rows[0].map(h => h.trim().replace(/^\uFEFF/, ''));
      const parsedData = rows.slice(1).filter(r => r.length > 1 && r[0]).map(rowArray => {
          let obj = {};
          headers.forEach((h, i) => { obj[h] = rowArray[i] ? rowArray[i].trim() : '' });
          return obj;
      });

      setPreviewModal({ show: true, data: parsedData, file: file, uploadType });
      event.target.value = ''; 
    };
    reader.readAsText(file);
  };

  const handleCellChange = (rowIndex, key, value) => {
    const newData = [...previewModal.data];
    newData[rowIndex][key] = value;
    setPreviewModal({ ...previewModal, data: newData });
  };

  const executeCsvUpload = async () => {
    setUploadingCsv(true);
    
    const csvString = convertToCSV(previewModal.data);
    const blob = new Blob([csvString], { type: 'text/csv' });
    
    const formData = new FormData();
    formData.append('file', blob, previewModal.file.name);
    
    let endpoint = '';
    if (previewModal.uploadType === 'SPRINT') {
        formData.append('orgId', targetUploadOrg); 
        endpoint = '/api/admin/upload-sprint-questions';
    } else if (previewModal.uploadType === 'TOOL') {
        endpoint = '/api/admin/upload-tool-questions';
    } else if (previewModal.uploadType === 'MATRIX') {
        formData.append('orgId', targetUploadOrg);
        endpoint = '/api/admin/upload-intervention-matrix';
    }

    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const res = await axios.post(`${baseURL}${endpoint}`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setAlertModal({ show: true, title: 'Upload Complete', message: res.data.message, type: 'success' });
    } catch (err) {
      setAlertModal({ show: true, title: 'Upload Failed', message: err.response?.data?.error || 'Server error', type: 'error' });
    } finally {
      setUploadingCsv(false);
      setPreviewModal({ show: false, data: [], file: null, uploadType: '' });
      if (previewModal.uploadType === 'SPRINT' || previewModal.uploadType === 'MATRIX') setTargetUploadOrg('');
    }
  };

  const fetchOrgs = async () => {
    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const res = await axios.get(`${baseURL}/api/admin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrgs(res.data);
    } catch (err) {
      setAlertModal({ show: true, title: 'Error', message: 'Failed to load organisations from server.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleApproveTier = (org, targetTier) => {
    const userEmail = org.profiles?.[0]?.email;
    if (!userEmail) {
      setAlertModal({ show: true, title: 'Error', message: 'Cannot approve: No valid user email attached to this organization.', type: 'error' });
      return;
    }

    setConfirmModal({
      show: true,
      title: `Approve Tier ${targetTier} Upgrade`,
      message: `Are you sure you want to approve Tier ${targetTier} Access for ${org.name} (${userEmail})? This will automatically send them an approval email.`,
      onConfirm: async () => {
        try {
          const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
          await axios.post(`${baseURL}/api/admin/approve-tier`, {
            orgId: org.id,
            userEmail: userEmail,
            isNewUser: false, 
            targetTier: targetTier
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          setAlertModal({ show: true, title: 'Success', message: 'Successfully approved and email notification sent!', type: 'success' });
          fetchOrgs(); 
        } catch (err) {
          setAlertModal({ show: true, title: 'Approval Failed', message: err.response?.data?.error || 'Failed to approve tier upgrade.', type: 'error' });
        }
      }
    });
  };

  const handleDowngradeTier = (org, targetTier) => {
    setConfirmModal({
      show: true,
      title: `Downgrade ${org.name}`,
      message: `Are you sure you want to downgrade this organization to Tier ${targetTier}? Their AI limits will be immediately reduced.`,
      onConfirm: async () => {
        try {
          const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
          await axios.post(`${baseURL}/api/admin/downgrade-tier`, { orgId: org.id, targetTier }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setAlertModal({ show: true, title: 'Success', message: 'Organization downgraded successfully.', type: 'success' });
          fetchOrgs();
        } catch (err) {
          setAlertModal({ show: true, title: 'Error', message: 'Failed to downgrade organization.', type: 'error' });
        }
      }
    });
  };

  const handleDeleteOrg = (org) => {
    setConfirmModal({
      show: true,
      title: `Delete ${org.name}`,
      message: `CRITICAL: Are you sure you want to permanently delete this organization and wipe all its users from Auth? This cannot be undone.`,
      onConfirm: async () => {
        try {
          const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
          await axios.delete(`${baseURL}/api/admin/delete-org/${org.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setAlertModal({ show: true, title: 'Deleted', message: 'Organization and users deleted successfully.', type: 'success' });
          fetchOrgs();
        } catch (err) {
          setAlertModal({ show: true, title: 'Error', message: 'Failed to delete organization.', type: 'error' });
        }
      }
    });
  };

  const handleExtend = async (e) => {
    e.preventDefault();
    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      await axios.post(`${baseURL}/api/admin/extend-trial`, {
        targetOrgId: selectedOrg.id,
        addDays: extendForm.addDays,
        newAiLimit: extendForm.newAiLimit,
        newRiskLimit: extendForm.newRiskLimit
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedOrg(null);
      setAlertModal({ show: true, title: 'Success', message: 'Limits and trial length updated successfully.', type: 'success' });
      fetchOrgs();
    } catch (err) {
      setAlertModal({ show: true, title: 'Error', message: err.response?.data?.error || 'Failed to extend trial', type: 'error' });
    }
  };

  const handlePromote = async (e) => {
    e.preventDefault();
    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      await axios.post(`${baseURL}/api/admin/promote`, {
        targetUserId: promoteOrg.profiles[0].id,
        privileges: selectedPrivileges
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPromoteOrg(null);
      setSelectedPrivileges([]);
      setAlertModal({ show: true, title: 'Success', message: 'User successfully promoted to Admin.', type: 'success' });
      fetchOrgs();
    } catch (err) {
      setAlertModal({ show: true, title: 'Error', message: err.response?.data?.error || 'Promotion failed', type: 'error' });
    }
  };

  const togglePrivilege = (privId) => setSelectedPrivileges(prev => prev.includes(privId) ? prev.filter(id => id !== privId) : [...prev, privId]);

  if (loading) return <div className="p-8 text-center text-slate-500">Loading System Admin...</div>;

  const promotedAdminsCount = orgs.filter(org => org.profiles?.[0]?.admin_role === 'PROMOTED_ADMIN').length;
  const isSuperAdmin = user?.admin_role === 'SUPER_ADMIN' || user?.is_system_admin === true;

  const getModalTitle = () => {
    if (previewModal.uploadType === 'SPRINT') return 'Preview & Edit: Client Sprint Intake';
    if (previewModal.uploadType === 'TOOL') return 'Preview & Edit: Global Tool Assessment';
    if (previewModal.uploadType === 'MATRIX') return 'Preview & Edit: AI Intervention Matrix';
    return 'Preview Data';
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      
      {/* ── GENERIC ALERT MODAL ── */}
      {alertModal.show && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 transform transition-all text-center">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center text-3xl mx-auto mb-4 ${
              alertModal.type === 'success' ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'
            }`}>
              {alertModal.type === 'success' ? '✅' : '❌'}
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">{alertModal.title}</h3>
            <p className="text-slate-500 mb-6 text-sm leading-relaxed">{alertModal.message}</p>
            <button onClick={() => setAlertModal({ show: false, title: '', message: '', type: 'success' })} className="w-full px-4 py-2.5 bg-slate-900 text-white font-bold rounded-lg hover:bg-slate-800 transition-colors">
              Acknowledge
            </button>
          </div>
        </div>
      )}

      {/* ── GENERIC CONFIRM MODAL ── */}
      {confirmModal.show && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 transform transition-all text-center">
            <div className="w-16 h-16 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-3xl mx-auto mb-4">ℹ️</div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">{confirmModal.title}</h3>
            <p className="text-slate-500 mb-6 text-sm leading-relaxed">{confirmModal.message}</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmModal({ show: false, title: '', message: '', onConfirm: null })} className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 font-bold rounded-lg hover:bg-slate-50 transition-colors">Cancel</button>
              <button onClick={() => { confirmModal.onConfirm(); setConfirmModal({ show: false, title: '', message: '', onConfirm: null }); }} className="flex-1 px-4 py-2.5 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 transition-colors">Confirm</button>
            </div>
          </div>
        </div>
      )}

      {/* ── CLIENT RESPONSES MODAL ── */}
      {responsesModal.show && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col max-h-[90vh]">
            <div className="p-6 border-b border-slate-200 flex justify-between items-center bg-slate-50 rounded-t-2xl">
              <div>
                <h3 className="text-xl font-bold text-slate-900">Sprint Responses: {responsesModal.orgName}</h3>
                <p className="text-sm text-slate-500">Review the baseline assessment data submitted by the client.</p>
              </div>
              <button onClick={() => setResponsesModal({ show: false, orgName: '', data: [] })} className="text-slate-400 hover:text-slate-600 text-2xl font-bold px-2 py-1">&times;</button>
            </div>
            
            <div className="p-8 overflow-y-auto flex-1 bg-white space-y-8">
              {responsesModal.data.length === 0 ? (
                <div className="text-center py-12 text-slate-500">No Sprint Questions have been assigned to this client yet.</div>
              ) : (
                responsesModal.data.map((q) => (
                  <div key={q.id} className="border border-slate-200 rounded-lg p-5 shadow-sm">
                    <div className="flex gap-2 items-start mb-3">
                      <span className="bg-blue-100 text-blue-800 text-xs font-bold px-2 py-1 rounded-md">{q.order_index}</span>
                      <h4 className="text-sm font-semibold text-slate-800 leading-tight">{q.question_text}</h4>
                    </div>
                    <div className={`p-4 rounded-lg text-sm ${q.answer_data ? 'bg-slate-50 border border-slate-200 text-slate-700 font-medium' : 'bg-red-50 border border-red-100 text-red-500 font-bold'}`}>
                      {q.answer_data ? (Array.isArray(q.answer_data) ? q.answer_data.join(', ') : q.answer_data) : 'Not Answered'}
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="p-4 border-t border-slate-200 bg-slate-50 rounded-b-2xl flex justify-end">
              <button onClick={() => setResponsesModal({ show: false, orgName: '', data: [] })} className="px-6 py-2 bg-slate-900 text-white font-bold rounded-lg hover:bg-slate-800 transition-colors">Close Report</button>
            </div>
          </div>
        </div>
      )}

      {/* ── EDITABLE CSV PREVIEW MODAL ── */}
      {previewModal.show && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl flex flex-col max-h-[90vh]">
            <div className="p-6 border-b border-slate-200 flex justify-between items-center bg-slate-50 rounded-t-2xl">
              <div>
                <h3 className="text-xl font-bold text-slate-900">
                  {getModalTitle()}
                </h3>
                <p className="text-sm text-slate-500">You can edit the fields directly in the table below before confirming the upload.</p>
              </div>
              <div className="bg-blue-100 text-blue-800 font-bold px-3 py-1 rounded-lg text-sm">
                {previewModal.data.length} Rows Found
              </div>
            </div>
            
            <div className="p-6 overflow-x-auto overflow-y-auto flex-1 bg-white">
              <table className="min-w-full divide-y divide-slate-200 border border-slate-200 rounded-lg overflow-hidden">
                <thead className="bg-slate-900 text-white">
                  <tr>
                    {previewModal.data.length > 0 && Object.keys(previewModal.data[0]).map((key, idx) => (
                      <th key={idx} className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider whitespace-nowrap">
                        {key}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {previewModal.data.map((row, rowIndex) => (
                    <tr key={rowIndex} className="hover:bg-blue-50 transition-colors">
                      {Object.keys(row).map((key, colIndex) => (
                        <td key={colIndex} className="px-2 py-2">
                          <input
                            type="text"
                            value={row[key] || ''}
                            onChange={(e) => handleCellChange(rowIndex, key, e.target.value)}
                            className="w-full min-w-[120px] bg-transparent border-0 focus:ring-2 focus:ring-blue-500 focus:bg-white rounded px-2 py-1.5 text-sm text-slate-800 transition-all shadow-sm hover:shadow-inner"
                            title={row[key]}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="p-6 border-t border-slate-200 bg-slate-50 rounded-b-2xl flex justify-end gap-3">
              <button onClick={() => setPreviewModal({ show: false, data: [], file: null, uploadType: '' })} className="px-6 py-2.5 border border-slate-300 text-slate-700 font-bold rounded-lg hover:bg-white transition-colors">
                Cancel
              </button>
              <button onClick={executeCsvUpload} disabled={uploadingCsv} className="px-6 py-2.5 bg-emerald-600 text-white font-bold rounded-lg hover:bg-emerald-700 transition-colors shadow-md disabled:opacity-50">
                {uploadingCsv ? 'Processing...' : 'Confirm & Upload Data'}
              </button>
            </div>
          </div>
        </div>
      )}


      <div className="flex items-center justify-between mb-4">
        <div>
          <button onClick={() => navigate('/dashboard')} className="text-sm font-medium text-slate-400 hover:text-blue-600 mb-3 flex items-center gap-2 transition-colors">
            ← Back to Dashboard
          </button>
          <h1 className="text-2xl font-bold text-slate-900">System Admin Dashboard</h1>
          <p className="text-slate-500 text-sm">Manage platform access, trial extensions, and Sprint approvals.</p>
        </div>
        {isSuperAdmin && (
          <div className="bg-purple-50 border border-purple-200 text-purple-700 px-4 py-2 rounded-lg text-sm font-semibold">
            Admin Slots Used: {promotedAdminsCount} / 3
          </div>
        )}
      </div>

      {isSuperAdmin && (
        <div className="flex gap-4 border-b border-slate-200 mb-8">
          <button onClick={() => setActiveTab('orgs')} className={`py-2 px-4 font-bold border-b-2 transition-colors ${activeTab === 'orgs' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
            Platform Users
          </button>
          <button onClick={() => setActiveTab('leads')} className={`py-2 px-4 font-bold border-b-2 flex items-center gap-2 transition-colors ${activeTab === 'leads' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
            General Inquiries
            {leads.filter(l => l.status === 'Pending').length > 0 && (
              <span className="bg-red-500 text-white text-[10px] px-2 py-0.5 rounded-full">{leads.filter(l => l.status === 'Pending').length}</span>
            )}
          </button>
          <button onClick={() => setActiveTab('graderLeads')} className={`py-2 px-4 font-bold border-b-2 flex items-center gap-2 transition-colors ${activeTab === 'graderLeads' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
            Risk Grader Leads
            {graderLeads.filter(l => l.status === 'Pending' || !l.status).length > 0 && (
              <span className="bg-amber-500 text-white text-[10px] px-2 py-0.5 rounded-full">{graderLeads.filter(l => l.status === 'Pending' || !l.status).length}</span>
            )}
          </button>
        </div>
      )}

      {/* Leads Tab Content */}
      {activeTab === 'leads' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Contact</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Intent</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date / Time</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {leads.map(lead => (
                <tr key={lead.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4 text-sm text-slate-900">
                    <div className="font-bold">{lead.full_name}</div>
                    <div className="text-slate-500">{lead.contact_email}</div>
                    <div className="text-xs text-blue-600 mt-1">{lead.org_name}</div>
                  </td>
                  <td className="px-6 py-4 text-sm font-semibold">
                    {lead.request_type === 'Discovery Call' ? '📞 Discovery Call' : '💼 Tier 3 Retainer'}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">
                    {lead.preferred_date ? `${lead.preferred_date} @ ${lead.preferred_time}` : 'ASAP'}
                  </td>
                  <td className="px-6 py-4 text-sm text-right">
                    <select 
                      className={`text-xs font-bold px-3 py-1 rounded-full outline-none border cursor-pointer ${lead.status === 'Pending' ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-emerald-50 text-emerald-800 border-emerald-200'}`}
                      value={lead.status}
                      onChange={async (e) => {
                        const newStatus = e.target.value;
                        setLeads(leads.map(l => l.id === lead.id ? { ...l, status: newStatus } : l));
                        try {
                          const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
                          await axios.patch(`${baseURL}/api/leads/${lead.id}/status`, { status: newStatus }, { headers: { Authorization: `Bearer ${token}` }});
                        } catch (err) {
                          fetchLeads(); 
                          alert("Failed to save status to database.");
                        }
                      }}
                    >
                      <option value="Pending">Pending</option>
                      <option value="Contacted">Contacted</option>
                      <option value="Converted">Converted</option>
                      <option value="Closed">Closed</option>
                    </select>
                  </td>
                </tr>
              ))}
              {leads.length === 0 && (
                <tr>
                  <td colSpan="4" className="px-6 py-8 text-center text-slate-500 text-sm">No inbound requests found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Grader Leads Tab Content */}
      {activeTab === 'graderLeads' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Prospect</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Organization</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Tool Assessed</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {graderLeads.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-slate-500 text-sm">No risk grader leads yet.</td>
                </tr>
              ) : (
                graderLeads.map((lead) => (
                  <tr key={lead.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 text-sm text-slate-600">{formatDate(lead.created_at)}</td>
                    <td className="px-6 py-4 text-sm text-slate-900">
                      <div className="font-bold">{lead.full_name}</div>
                      <div className="text-slate-500">{lead.contact_email}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-700">{lead.org_name}</td>
                    <td className="px-6 py-4 text-sm">
                      <div className="text-slate-600 mb-1">{lead.tool_name}</div>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-bold border ${
                        lead.risk_grade === 'A' || lead.risk_grade === 'B' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                        lead.risk_grade === 'C' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                        'bg-red-50 text-red-700 border-red-200'
                      }`}>
                        Grade {lead.risk_grade} ({lead.risk_score})
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-right">
                      <select 
                        className={`text-xs font-bold px-3 py-1.5 rounded-full outline-none border cursor-pointer ${
                          lead.status === 'Contacted' ? 'bg-blue-50 text-blue-800 border-blue-200' : 
                          lead.status === 'Converted' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 
                          lead.status === 'Closed' ? 'bg-slate-100 text-slate-600 border-slate-300' : 
                          'bg-amber-50 text-amber-800 border-amber-200'
                        }`}
                        value={lead.status || 'Pending'}
                        onChange={(e) => updateGraderStatus(lead.id, e.target.value)}
                      >
                        <option value="Pending">Pending</option>
                        <option value="Contacted">Contacted</option>
                        <option value="Converted">Converted</option>
                        <option value="Closed">Closed</option>
                      </select>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Orgs Tab Content */}
      {activeTab === 'orgs' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Organisation / User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Role / Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Tier / Limits</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {orgs.map(org => {
                const profile = org.profiles?.[0];
                const isAdmin = profile?.is_system_admin || profile?.admin_role === 'SUPER_ADMIN'; 
                const isSuperAdminTarget = profile?.admin_role === 'SUPER_ADMIN';
                
                const isExpired = org.tier === 0 && org.trial_end_date && new Date(org.trial_end_date) < new Date();
                
                const isPendingTier1 = org.upgrade_status === 'PENDING_TIER_1';
                const isPendingTier2 = org.upgrade_status === 'PENDING_TIER_2';

                return (
                  <tr key={org.id} className={`hover:bg-slate-50 ${(isPendingTier1 || isPendingTier2) ? 'bg-amber-50/30' : ''}`}>
                    <td className="px-6 py-4">
                      <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                        {org.name}
                        {(isPendingTier1 || isPendingTier2) && (
                           <span className="flex h-2 w-2 relative">
                             <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                             <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
                           </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500">{profile?.email || 'No user attached'}</div>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="mb-1 flex gap-2">
                        {isAdmin ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">{isSuperAdminTarget ? '👑 Super Admin' : '🛡️ Promoted Admin'}</span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-800">Org Admin</span>
                        )}
                      </div>
                      {isSuperAdminTarget ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-md bg-indigo-50 text-indigo-700 text-xs font-medium">Platform Owner</span>
                      ) : isPendingTier1 ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-md bg-amber-100 text-amber-800 text-xs font-bold border border-amber-200">Pending Tier 1</span>
                      ) : isPendingTier2 ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-md bg-amber-100 text-amber-800 text-xs font-bold border border-amber-200">Pending Tier 2</span>
                      ) : isExpired ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-md bg-red-50 text-red-700 text-xs font-medium">Trial Expired</span>
                      ) : org.tier > 0 ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 text-xs font-medium">✨ Tier {org.tier} Active</span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-md bg-blue-50 text-blue-700 text-xs font-medium">⏱️ Free Trial</span>
                      )}
                    </td>
                    
                    <td className="px-6 py-4 text-sm text-slate-600">
                      <div className="mb-1">
                        {isSuperAdminTarget ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-purple-100 text-purple-800">
                            ♾️ Unlimited
                          </span>
                        ) : (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${org.tier >= 2 ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-600'}`}>
                            Tier {org.tier === 0 ? 'Trial' : org.tier}
                          </span>
                        )}
                      </div>
                      {!isSuperAdminTarget && (
                        <div className="text-xs mt-1">
                          <div>AI Tools: <span className="font-semibold">{org.max_ai_tools}</span></div>
                          <div>Analyses: <span className="font-semibold">{org.max_risk_analyses}</span></div>
                          {org.tier >= 2 && (
                            <div>Reviews: <span className="font-semibold text-purple-600">{org.report_reviews_available || 0}</span></div>
                          )}
                        </div>
                      )}
                    </td>
                    
                    <td className="px-6 py-4 text-sm text-right flex flex-col gap-2 items-end">
                      {isPendingTier1 && <button onClick={() => handleApproveTier(org, 1)} className="bg-amber-500 hover:bg-amber-600 text-white font-bold shadow-sm px-3 py-1.5 rounded transition w-36 text-center text-xs">Approve Tier 1</button>}
                      {isPendingTier2 && <button onClick={() => handleApproveTier(org, 2)} className="bg-amber-500 hover:bg-amber-600 text-white font-bold shadow-sm px-3 py-1.5 rounded transition w-36 text-center text-xs">Approve Tier 2</button>}

                      {!isSuperAdminTarget && (
                        <>
                          {org.tier < 3 && (
                            <div className="flex items-center gap-1 w-full justify-end">
                              <select id={`up-${org.id}`} className="border border-emerald-200 text-emerald-800 text-xs rounded px-1 py-1.5 bg-emerald-50 focus:outline-none focus:ring-1 focus:ring-emerald-400">
                                {[1, 2, 3].filter(t => t > org.tier).map(t => <option key={t} value={t}>Tier {t}</option>)}
                              </select>
                              <button onClick={() => {
                                  const targetTier = parseInt(document.getElementById(`up-${org.id}`).value);
                                  handleApproveTier(org, targetTier);
                                }} className="text-white font-medium px-3 py-1.5 bg-emerald-600 rounded hover:bg-emerald-700 transition w-24 text-center text-xs">
                                  Upgrade
                              </button>
                            </div>
                          )}

                          {org.tier > 0 && (
                            <div className="flex items-center gap-1 w-full justify-end">
                              <select id={`down-${org.id}`} className="border border-amber-200 text-amber-800 text-xs rounded px-1 py-1.5 bg-amber-50 focus:outline-none focus:ring-1 focus:ring-amber-400">
                                {[0, 1, 2].filter(t => t < org.tier).map(t => <option key={t} value={t}>Tier {t}</option>)}
                              </select>
                              <button onClick={() => {
                                  const targetTier = parseInt(document.getElementById(`down-${org.id}`).value);
                                  handleDowngradeTier(org, targetTier);
                                }} className="text-white font-medium px-3 py-1.5 bg-amber-600 rounded hover:bg-amber-700 transition w-24 text-center text-xs">
                                  Downgrade
                              </button>
                            </div>
                          )}

                          <button onClick={() => setSelectedOrg(org)} className="text-blue-600 hover:text-white font-medium px-3 py-1 border border-blue-200 rounded hover:bg-blue-600 transition w-36 text-center text-xs mt-1">Adjust Limits</button>

                          <button onClick={() => handleImpersonate(org.id)} className="text-purple-600 hover:text-white font-medium px-3 py-1 border border-purple-200 rounded hover:bg-purple-600 transition w-36 text-center text-xs">
                            View Dashboard
                          </button>

                          {org.tier >= 2 && (
                            <button onClick={() => handleViewResponses(org.id, org.name)} className="text-emerald-600 hover:text-white font-medium px-3 py-1 border border-emerald-200 rounded hover:bg-emerald-600 transition w-36 text-center text-xs">
                              View Sprint Answers
                            </button>
                          )}

                          <button onClick={() => handleDeleteOrg(org)} className="text-white font-medium px-3 py-1 bg-red-600 rounded hover:bg-red-700 transition w-36 text-center text-xs mt-1">
                              Delete Client
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Global Tool Assessment block */}
      {isSuperAdmin && activeTab === 'orgs' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-4 flex items-center justify-between mt-8">
          <div className="max-w-xl">
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2"><span>🛠️</span> Global Tool Assessment Framework</h2>
            <p className="text-sm text-slate-500 mt-1">Upload the master CSV for the dynamic AI tool questions (Applies to all Free Trial & Tier 1 users).</p>
          </div>
          <div className="flex items-center gap-3">
            <input type="file" id="toolCsvUpload" accept=".csv" className="hidden" onChange={(e) => handleFileSelectionForPreview(e, 'TOOL')} disabled={uploadingCsv}/>
            <label htmlFor="toolCsvUpload" className={`cursor-pointer font-bold py-2.5 px-6 rounded-lg transition-colors shadow-sm whitespace-nowrap ${uploadingCsv ? 'bg-slate-300 text-slate-500 cursor-not-allowed' : 'bg-slate-900 hover:bg-slate-800 text-white'}`}>
              {uploadingCsv ? 'Processing...' : 'Upload Master CSV'}
            </label>
          </div>
        </div>
      )}

      {/* Client-Specific Configurations Block with Dual Upload Buttons */}
      {isSuperAdmin && activeTab === 'orgs' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-8">
          <div className="flex items-center justify-between mb-4 border-b border-slate-100 pb-4">
            <div className="max-w-xl">
              <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2"><span>📋</span> Client-Specific Sprint Configurations</h2>
              <p className="text-sm text-slate-500 mt-1">Select a Tier 2 client to assign custom Sprint Intakes or AI Intervention Matrices.</p>
            </div>
            <select 
              className="border border-slate-300 rounded-lg px-4 py-2.5 text-sm font-medium text-slate-700 bg-slate-50 outline-none focus:border-blue-500 w-64"
              value={targetUploadOrg}
              onChange={(e) => setTargetUploadOrg(e.target.value)}
            >
              <option value="">-- Select Client --</option>
              {orgs.filter(o => o.tier >= 1 || o.upgrade_status === 'PENDING_TIER_2').map(org => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
          </div>

          {/* Action Buttons Row */}
          <div className="flex items-center justify-end gap-3">
            <input type="file" id="csvUploadSprint" accept=".csv" className="hidden" onChange={(e) => handleFileSelectionForPreview(e, 'SPRINT')} disabled={uploadingCsv || !targetUploadOrg}/>
            <label htmlFor="csvUploadSprint" className={`cursor-pointer font-bold py-2 px-4 rounded-lg transition-colors shadow-sm whitespace-nowrap text-sm ${uploadingCsv || !targetUploadOrg ? 'bg-slate-300 text-slate-500 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}>
              {uploadingCsv ? 'Processing...' : 'Upload Sprint Intake'}
            </label>

            <input type="file" id="csvUploadMatrix" accept=".csv" className="hidden" onChange={(e) => handleFileSelectionForPreview(e, 'MATRIX')} disabled={uploadingCsv || !targetUploadOrg}/>
            <label htmlFor="csvUploadMatrix" className={`cursor-pointer font-bold py-2 px-4 rounded-lg transition-colors shadow-sm whitespace-nowrap text-sm ${uploadingCsv || !targetUploadOrg ? 'bg-slate-300 text-slate-500 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 text-white'}`}>
              {uploadingCsv ? 'Processing...' : 'Upload AI Matrix'}
            </label>
          </div>
        </div>
      )}

      {isSuperAdmin && activeTab === 'orgs' && (
        <FrameworkUpload />
      )}

      {selectedOrg && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-1">Adjust Access: {selectedOrg.name}</h3>
            <p className="text-sm text-slate-500 mb-6">Current End Date: {new Date(selectedOrg.trial_end_date).toLocaleDateString()}</p>
            <form onSubmit={handleExtend} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Days to Add to Trial</label>
                <input type="number" required min="1" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" value={extendForm.addDays} onChange={e => setExtendForm({...extendForm, addDays: e.target.value})} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">New AI Limit</label>
                  <input type="number" required min="1" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" value={extendForm.newAiLimit} onChange={e => setExtendForm({...extendForm, newAiLimit: e.target.value})} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">New Risk Limit</label>
                  <input type="number" required min="1" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" value={extendForm.newRiskLimit} onChange={e => setExtendForm({...extendForm, newRiskLimit: e.target.value})} />
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setSelectedOrg(null)} className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 font-medium">Cancel</button>
                <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">Confirm</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {promoteOrg && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-1">Promote to Admin</h3>
            <p className="text-sm text-slate-500 mb-6">Assign operational privileges to {promoteOrg.profiles?.[0]?.email}</p>
            <form onSubmit={handlePromote} className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="text-sm font-semibold text-slate-700 mb-3">Select Privileges:</h4>
                <div className="space-y-3">
                  {AVAILABLE_PRIVILEGES.map(priv => (
                    <label key={priv.id} className="flex items-center gap-3 cursor-pointer">
                      <input type="checkbox" className="w-4 h-4 text-purple-600 rounded border-slate-300 focus:ring-purple-500" checked={selectedPrivileges.includes(priv.id)} onChange={() => togglePrivilege(priv.id)} />
                      <span className="text-sm text-slate-700">{priv.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setPromoteOrg(null)} className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 font-medium">Cancel</button>
                <button type="submit" className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium">Promote User</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}